import { useTranslation } from "react-i18next";
import { BrutalBadge } from "@/components/ui/BrutalBadge";
import { JobFormData } from "@/pages/dashboard/JobCreate";
import { contractTypes, workModes, salaryPeriods, inclusivityTags, inclusiveBenefits, applicationMethods } from "@/data/jobOptions";
import { 
  Briefcase, MapPin, Clock, DollarSign, FileText, 
  Heart, Send, CheckCircle2, Building2, Laptop, Globe 
} from "lucide-react";

interface JobFormStep5Props {
  formData: JobFormData;
}

export function JobFormStep5({ formData }: JobFormStep5Props) {
  const { t } = useTranslation();

  const contractData = contractTypes.find(c => c.value === formData.contract_type);
  const workModeData = workModes.find(w => w.value === formData.work_mode);
  const salaryPeriodData = salaryPeriods.find(s => s.value === formData.salary_period);
  const applicationMethodData = applicationMethods.find(a => a.value === formData.application_method);

  const formatSalary = () => {
    if (!formData.salary_range_min && !formData.salary_range_max) return null;
    const min = formData.salary_range_min?.toLocaleString('it-IT');
    const max = formData.salary_range_max?.toLocaleString('it-IT');
    const periodLabel = salaryPeriodData ? t(salaryPeriodData.labelKey) : '';
    if (min && max) return `€${min} - €${max} ${periodLabel}`;
    if (min) return `${t("jobs.form.salaryFrom", { amount: min })} ${periodLabel}`;
    if (max) return `${t("jobs.form.salaryUpTo", { amount: max })} ${periodLabel}`;
    return null;
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-xl font-bold text-dark mb-2">
          {t("jobs.form.step5Title")}
        </h2>
        <p className="text-muted-foreground">
          {t("jobs.form.step5Desc")}
        </p>
      </div>

      {/* Preview Card */}
      <div className="border-[3px] border-dark p-6 bg-surface">
        {/* Header */}
        <div className="flex items-start gap-4 mb-4 pb-4 border-b-[2px] border-dark">
          <div className="w-14 h-14 bg-primary/20 border-[2px] border-dark flex items-center justify-center flex-shrink-0">
            <Briefcase className="w-7 h-7 text-primary" />
          </div>
          <div>
            <h3 className="font-heading text-2xl font-bold text-dark mb-1">
              {formData.title || t("jobs.form.untitled")}
            </h3>
            <p className="text-muted-foreground">{t("jobs.form.yourCompany")}</p>
          </div>
        </div>

        {/* Meta info */}
        <div className="flex flex-wrap gap-3 mb-4">
          <BrutalBadge variant="secondary">
            <MapPin className="w-3 h-3 mr-1" />
            {formData.location_city || '-'}
          </BrutalBadge>
          <BrutalBadge variant="secondary">
            <Clock className="w-3 h-3 mr-1" />
            {contractData ? t(contractData.labelKey) : formData.contract_type}
          </BrutalBadge>
          <BrutalBadge variant="secondary">
            {formData.work_mode === 'remoto' ? <Laptop className="w-3 h-3 mr-1" /> : 
             formData.work_mode === 'ibrido' ? <Globe className="w-3 h-3 mr-1" /> : 
             <Building2 className="w-3 h-3 mr-1" />}
            {workModeData ? t(workModeData.labelKey) : formData.work_mode}
          </BrutalBadge>
          {formatSalary() && (
            <BrutalBadge variant="secondary">
              <DollarSign className="w-3 h-3 mr-1" />
              {formatSalary()}
            </BrutalBadge>
          )}
        </div>

        {/* Inclusivity Tags */}
        {formData.inclusivity_tags.length > 0 && (
          <div className="mb-4 pb-4 border-b-[2px] border-dark">
            <div className="flex flex-wrap gap-2">
              {formData.inclusivity_tags.map((tag) => {
                const tagInfo = inclusivityTags.find(t => t.value === tag);
                return (
                  <BrutalBadge key={tag} variant="default">
                    {tagInfo?.icon} {tagInfo ? t(tagInfo.labelKey) : tag}
                  </BrutalBadge>
                );
              })}
            </div>
          </div>
        )}

        {/* Description Preview */}
        <div className="mb-4 pb-4 border-b-[2px] border-dark">
          <h4 className="font-heading font-bold text-dark mb-2 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            {t("jobs.detail.description")}
          </h4>
          <div 
            className="text-sm text-muted-foreground line-clamp-4"
            dangerouslySetInnerHTML={{ __html: formData.description || '-' }}
          />
        </div>

        {/* Skills */}
        {(formData.required_skills.length > 0 || formData.nice_to_have_skills.length > 0) && (
          <div className="mb-4 pb-4 border-b-[2px] border-dark">
            {formData.required_skills.length > 0 && (
              <div className="mb-3">
                <h4 className="font-semibold text-dark mb-2 text-sm">
                  {t("jobs.detail.requiredSkills")}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {formData.required_skills.map((skill, i) => (
                    <BrutalBadge key={i} variant="default">{skill}</BrutalBadge>
                  ))}
                </div>
              </div>
            )}
            {formData.nice_to_have_skills.length > 0 && (
              <div>
                <h4 className="font-semibold text-dark mb-2 text-sm">
                  {t("jobs.detail.niceToHave")}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {formData.nice_to_have_skills.map((skill, i) => (
                    <BrutalBadge key={i} variant="secondary">{skill}</BrutalBadge>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Benefits */}
        {formData.inclusive_benefits.length > 0 && (
          <div className="mb-4 pb-4 border-b-[2px] border-dark">
            <h4 className="font-heading font-bold text-dark mb-2 flex items-center gap-2">
              <Heart className="w-4 h-4 text-primary" />
              {t("jobs.detail.inclusiveBenefits")}
            </h4>
            <ul className="space-y-1">
              {formData.inclusive_benefits.map((benefit, i) => {
                const benefitInfo = inclusiveBenefits.find(b => b.value === benefit);
                return (
                  <li key={i} className="flex items-center gap-2 text-sm text-dark">
                    <CheckCircle2 className="w-4 h-4 text-success flex-shrink-0" />
                    {benefitInfo ? `${benefitInfo.icon} ${t(benefitInfo.labelKey)}` : benefit}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Application Method */}
        <div>
          <h4 className="font-heading font-bold text-dark mb-2 flex items-center gap-2">
            <Send className="w-4 h-4" />
            {t("jobs.create.applicationMethod")}
          </h4>
          <p className="text-sm text-muted-foreground">
            {applicationMethodData ? t(applicationMethodData.labelKey) : ''}
            {formData.application_method === 'email' && formData.application_email && (
              <span> ({formData.application_email})</span>
            )}
            {formData.application_method === 'external_link' && formData.application_url && (
              <span> ({formData.application_url})</span>
            )}
          </p>
        </div>
      </div>

      {/* Confirmation message */}
      <div className="p-4 bg-success/10 border-[2px] border-success">
        <p className="text-sm text-dark flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-success" />
          {t("jobs.form.confirmMessage")}
        </p>
      </div>
    </div>
  );
}
